import React, { useEffect, useState } from "react";
import { MessageCircle, X, Copy, Check, Send, Loader2 } from "lucide-react";
import { buildWhatsAppMessage } from "../lib/whatsappMessage";
import { api } from "../lib/api";

/**
 * "Assign Task via WhatsApp" — Command Center/CMO/Campaign Playbook's
 * lightweight delegation utility. Mints a secure, no-login share link
 * first (`api.assignLinkMint` — resolves server-side only, never exposes
 * customer_id), then builds a deterministic, editable message embedding
 * that link. The founder edits it if they wish, then either opens
 * WhatsApp's native contact picker (no phone number ever collected/
 * stored by C-ORE) or copies the message as a fallback. Sharing a task
 * never changes its status.
 */
export default function AssignTaskModal({ item, onClose }) {
  const [message, setMessage] = useState("");
  const [copied, setCopied] = useState(false);
  const [minting, setMinting] = useState(false);
  const [mintError, setMintError] = useState("");

  useEffect(() => {
    if (!item) return;
    setCopied(false);
    setMintError("");
    setMessage(buildWhatsAppMessage(item));
    const ruleId = item.rule_id || item.source_rule_id;
    if (!ruleId) return;
    setMinting(true);
    api.assignLinkMint(ruleId).then((res) => {
      setMinting(false);
      if (res.ok && res.data?.token) {
        const shareUrl = `${window.location.origin}/shared/task/${res.data.token}`;
        setMessage(buildWhatsAppMessage(item, shareUrl));
      } else {
        setMintError("Could not create a secure task link — you can still send the task details below.");
      }
    });
  }, [item]);

  if (!item) return null;

  const handleSend = () => {
    // Build + open synchronously (before any await) so browsers never
    // treat this as a blocked popup.
    const url = `https://api.whatsapp.com/send?text=${encodeURIComponent(message)}`;
    window.open(url, "_blank", "noopener,noreferrer");
    // Best-effort activity log only — never blocks/gates the share above.
    api.commandCenterTaskShared({
      ruleId: item.rule_id || item.source_rule_id,
      decisionId: item.id,
      title: item.title,
    });
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(message);
      setCopied(true);
    } catch (_e) {
      const ta = document.createElement("textarea");
      ta.value = message;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); setCopied(true); } catch (_e2) { /* no-op */ }
      document.body.removeChild(ta);
    }
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink-950/50 backdrop-blur-sm px-4"
      data-testid="assign-task-modal"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-lg rounded-2xl bg-white shadow-modal max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute right-4 top-4 text-ink-400 hover:text-ink-600"
          data-testid="assign-task-close"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="px-6 pt-6 pb-4 border-b border-ink-100">
          <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-status-onTrack-text">
            <MessageCircle className="h-3.5 w-3.5" /> Assign Task
          </div>
          <h2 className="mt-1 text-[16px] font-display font-bold text-blue-800">
            Share this task as a ready-to-send WhatsApp message.
          </h2>
          <p className="mt-1 text-[12px] text-ink-500">
            You choose the recipient inside WhatsApp — C-ORE never asks for or stores a phone number.
          </p>
        </div>

        <div className="px-6 py-4">
          {minting && (
            <div className="mb-2 flex items-center gap-1.5 text-[12px] text-ink-500" data-testid="assign-task-minting">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Creating a secure task link…
            </div>
          )}
          {mintError && (
            <div className="mb-2 text-[12px] text-status-drifting-text" data-testid="assign-task-mint-error">{mintError}</div>
          )}
          <label className="text-[11px] font-semibold uppercase tracking-wider text-ink-400">Message</label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={11}
            className="mt-1.5 w-full rounded-xl border border-ink-200 p-3 text-[13px] text-ink-800 leading-snug focus:outline-none focus:ring-2 focus:ring-blue-800/30 resize-none"
            data-testid="assign-task-textarea"
          />
        </div>

        <div className="px-6 pb-6 pt-1 flex flex-wrap items-center gap-2">
          <button
            onClick={handleSend}
            className="flex-1 min-w-[170px] inline-flex items-center justify-center gap-1.5 rounded-lg bg-status-onTrack px-4 py-3 text-[13px] font-semibold text-white hover:bg-status-onTrack-text transition-colors"
            data-testid="assign-task-send-whatsapp"
          >
            <Send className="h-4 w-4" /> Send via WhatsApp
          </button>
          <button
            onClick={handleCopy}
            className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-ink-200 px-4 py-3 text-[13px] font-semibold text-ink-700 hover:bg-ink-50 transition-colors"
            data-testid="assign-task-copy"
          >
            {copied ? <Check className="h-4 w-4 text-status-onTrack" /> : <Copy className="h-4 w-4" />}
            {copied ? "Copied" : "Copy Message"}
          </button>
          <button
            onClick={onClose}
            className="inline-flex items-center justify-center gap-1.5 rounded-lg px-3 py-3 text-[13px] font-medium text-ink-500 hover:text-ink-700"
            data-testid="assign-task-cancel"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
